import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '@/features/auth/providers/AuthProvider';
import type { UserRole } from '@/features/auth/types';
import useGetOfficeList from '@/features/office/hooks/useGetOfficeList';
import type { Office } from '@/features/office/types';
import useOpenSuggestionCount from '@/features/suggestions/hooks/useOpenSuggestionCount';
import useGetUserRole from '@/features/user/hooks/useGetUserRole';
import { getStrictContext } from '@/lib/get-strict-context';
import useGetPreferredOffice from '@/shared/hooks/useGetPreferredOffice';

interface SidebarContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
}

export interface SidebarDataValue {
  role: UserRole | null;
  roleLoading: boolean;
  preferredOffice: string;
  officeLoading: boolean;
  officeList: Office[];
  openSuggestions: number;
}

const [SidebarContextProvider, useSidebar] =
  getStrictContext<SidebarContextValue>('SidebarProvider');

const [SidebarDataContextProvider, useSidebarData] =
  getStrictContext<SidebarDataValue>('SidebarData');

function managedOfficeIds(offices: Office[], role: string | null | undefined, userId?: string) {
  if (!userId || (role !== 'admin' && role !== 'officeManager')) return [];
  return offices
    .filter(office => !office.disabled)
    .filter(office => role === 'admin' || office.managerIds?.includes(userId))
    .map(office => office.id);
}

export function SidebarProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const uid = user?.uid;
  const [open, setOpen] = useState(false);

  const toggle = useCallback(() => {
    setOpen(prev => !prev);
  }, []);

  const { role, isLoading: roleLoading } = useGetUserRole(uid);
  const { preferredOffice, isLoading: officeLoading } = useGetPreferredOffice(uid);
  const officeList = useGetOfficeList(Boolean(uid));
  const openSuggestions = useOpenSuggestionCount(managedOfficeIds(officeList, role, uid));

  const ui = useMemo(() => ({ open, setOpen, toggle }), [open, setOpen, toggle]);
  const data = useMemo<SidebarDataValue>(
    () => ({
      role,
      roleLoading,
      preferredOffice,
      officeLoading,
      officeList,
      openSuggestions,
    }),
    [role, roleLoading, preferredOffice, officeLoading, officeList, openSuggestions]
  );

  return (
    <SidebarContextProvider value={ui}>
      <SidebarDataContextProvider value={data}>{children}</SidebarDataContextProvider>
    </SidebarContextProvider>
  );
}

export { useSidebar, useSidebarData };
